// src/components/DemocracyThreatCard.jsx
import React, { useState } from 'react';
import { AlertTriangle, Info, Scale, Radio, Users, FileText, Globe, ExternalLink } from 'lucide-react';
import CardContainer from './CardContainer';

const DemocracyThreatCard = ({ 
  quote, 
  pillars = [], 
  defaultExpanded = false,
  embedded = false,
  isMobile = false // Added isMobile prop with default false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  // Calculate overall threat level
  const getThreatLevel = () => {
    if (!pillars || pillars.length === 0) return { level: 'Unknown', color: 'gray', percentage: 0 };
    
    const impactLevels = {
      'severe': 3,
      'moderate': 2,
      'mild': 1
    };
    
    const totalImpact = pillars.reduce((sum, pillar) => {
      return sum + (impactLevels[pillar.impact_level?.toLowerCase()] || 0);
    }, 0);
    
    const maxPossibleImpact = pillars.length * 3;
    const percentage = Math.round((totalImpact / maxPossibleImpact) * 100);
    
    if (percentage >= 75) return { level: 'Severe', color: 'red', percentage };
    if (percentage >= 50) return { level: 'Moderate', color: 'orange', percentage };
    if (percentage >= 25) return { level: 'Mild', color: 'yellow', percentage };
    return { level: 'Minimal', color: 'green', percentage };
  };
  
  const threatInfo = getThreatLevel();
  
  // Organize pillars by impact level
  const severePillars = pillars.filter(p => p.impact_level?.toLowerCase() === 'severe');
  const moderatePillars = pillars.filter(p => p.impact_level?.toLowerCase() === 'moderate');
  const mildPillars = pillars.filter(p => p.impact_level?.toLowerCase() === 'mild');
  
  // Get color classes based on threat level
  const getColorClasses = (level) => {
    switch(level?.toLowerCase()) {
      case 'severe': return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        indicator: 'bg-red-600'
      };
      case 'moderate': return {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        indicator: 'bg-orange-500'
      };
      case 'mild': return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        indicator: 'bg-yellow-500'
      };
      default: return {
        bg: 'bg-gray-50',
        border: 'border-gray-200',
        text: 'text-gray-800',
        indicator: 'bg-gray-400'
      };
    }
  };
  
  const threatColors = getColorClasses(threatInfo.level);
  
  // Get icon for democratic pillar
  const getPillarIcon = (pillarName) => {
    if (!pillarName) return null;
    
    const name = pillarName.toLowerCase();
    if (name.includes('judicial') || name.includes('law')) return <Scale size={14} className="mr-1" />;
    if (name.includes('election') || name.includes('vote')) return <Users size={14} className="mr-1" />;
    if (name.includes('press') || name.includes('media')) return <FileText size={14} className="mr-1" />;
    return <Globe size={14} className="mr-1" />;
  };
  
  // If no pillars are provided, don't render anything
  if (pillars.length === 0) {
    return null;
  }

  // Header component
  const header = (
    <div>
      <span className="inline-block px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full mb-2">
        Democracy Assessment
      </span>
      <h2 className="text-lg font-semibold text-gray-800">Threat to Democratic Norms</h2>
    </div>
  );

  // Footer component (only for standalone mode)
  const footer = !embedded ? (
    <div className="flex justify-between text-xs text-gray-500">
      <div>
        Source: <a href="https://freedomhouse.org/report/freedom-world" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Freedom House</a>, 
        <a href="https://v-dem.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">V-Dem Institute</a>
      </div>
      <div>Last assessment: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
    </div>
  ) : null;
  
  // Content component
  const content = (
    <>
      {/* Threat Level Indicator */}
      <div className={`${threatColors.bg} p-3 border-b ${threatColors.border} flex items-center`}>
        <AlertTriangle size={18} className={threatColors.text + " mr-2"} />
        <div className="flex-grow">
          <span className={`text-sm font-medium ${threatColors.text}`}>Democracy Threat Level: {threatInfo.level}</span>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1">
            <div className={`${threatColors.indicator} h-2.5 rounded-full`} style={{ width: `${threatInfo.percentage}%` }}></div>
          </div>
        </div>
      </div>
      
      {/* Pillars Summary */}
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Democratic Pillars Threatened:</h3>
        <div className="flex flex-wrap gap-2">
          {pillars.map((pillar) => {
            const colors = getColorClasses(pillar.impact_level);
            return (
              <div key={pillar.id} className={`flex items-center ${colors.bg} ${colors.text} px-2 py-1 rounded-md text-xs`}>
                {getPillarIcon(pillar.name)}
                {pillar.name}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Info size={18} className="text-blue-600" />
              </div>
            </div>
            <div className="flex-grow">
              <h3 className="flex items-center text-md font-medium text-gray-800 mb-2">
                Detailed Threat Analysis
              </h3>
              
              {/* Severe Threats */}
              {severePillars.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-red-800 mb-2">Severe Threats</h4>
                  <div className="space-y-2">
                    {severePillars.map(pillar => (
                      <div key={pillar.id} className="bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex items-start">
                          <div className="bg-red-100 p-1 rounded-full mr-2 mt-0.5">
                            {getPillarIcon(pillar.name) || <AlertTriangle size={12} className="text-red-800" />}
                          </div>
                          <div>
                            <div className="flex items-center">
                              <h5 className="font-medium text-red-800 text-sm">{pillar.name}</h5>
                              {pillar.source_url && (
                                <a 
                                  href={pillar.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-red-700 hover:text-red-900 inline-flex items-center"
                                >
                                  <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mt-1">{pillar.justification}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Moderate Threats */}
              {moderatePillars.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-orange-800 mb-2">Moderate Threats</h4>
                  <div className="space-y-2">
                    {moderatePillars.map(pillar => (
                      <div key={pillar.id} className="bg-orange-50 border border-orange-200 rounded-md p-3">
                        <div className="flex items-start">
                          <div className="bg-orange-100 p-1 rounded-full mr-2 mt-0.5">
                            {getPillarIcon(pillar.name) || <AlertTriangle size={12} className="text-orange-800" />}
                          </div>
                          <div>
                            <div className="flex items-center">
                              <h5 className="font-medium text-orange-800 text-sm">{pillar.name}</h5>
                              {pillar.source_url && (
                                <a 
                                  href={pillar.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-orange-700 hover:text-orange-900 inline-flex items-center"
                                >
                                  <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mt-1">{pillar.justification}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Mild Threats */}
              {mildPillars.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-yellow-800 mb-2">Mild Threats</h4>
                  <div className="space-y-2">
                    {mildPillars.map(pillar => (
                      <div key={pillar.id} className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <div className="flex items-start">
                          <div className="bg-yellow-100 p-1 rounded-full mr-2 mt-0.5">
                            {getPillarIcon(pillar.name) || <AlertTriangle size={12} className="text-yellow-800" />}
                          </div>
                          <div>
                            <div className="flex items-center">
                              <h5 className="font-medium text-yellow-800 text-sm">{pillar.name}</h5>
                              {pillar.source_url && (
                                <a 
                                  href={pillar.source_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 text-yellow-700 hover:text-yellow-900 inline-flex items-center"
                                >
                                  <ExternalLink size={10} />
                                </a>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 mt-1">{pillar.justification}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Disinformation Patterns */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Related Disinformation Patterns:</h4>
                <div className="bg-white border border-gray-200 rounded-md p-3 space-y-2">
                  {severePillars.length > 0 && (
                    <div className="flex items-start">
                      <Radio size={14} className="text-red-600 mr-2 mt-1" />
                      <div>
                        <span className="text-sm font-medium">Attack on institutional credibility</span>
                        {severePillars.map(pillar => (
                          <p key={pillar.id} className="text-xs text-gray-600 mt-0.5">
                            Undermines trust in {pillar.name.toLowerCase()} through false claims
                            {pillar.source_url && (
                              <a 
                                href={pillar.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ml-1 text-blue-600 hover:underline"
                              >
                                [source]
                              </a>
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {moderatePillars.length > 0 && (
                    <div className="flex items-start">
                      <Radio size={14} className="text-orange-500 mr-2 mt-1" />
                      <div>
                        <span className="text-sm font-medium">False historical revisionism</span>
                        {moderatePillars.map(pillar => (
                          <p key={pillar.id} className="text-xs text-gray-600 mt-0.5">
                            Distorts history to justify attacks on {pillar.name.toLowerCase()}
                            {pillar.source_url && (
                              <a 
                                href={pillar.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ml-1 text-blue-600 hover:underline"
                              >
                                [source]
                              </a>
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {mildPillars.length > 0 && (
                    <div className="flex items-start">
                      <Radio size={14} className="text-yellow-600 mr-2 mt-1" />
                      <div>
                        <span className="text-sm font-medium">Preparing for institutional capture</span>
                        {mildPillars.map(pillar => (
                          <p key={pillar.id} className="text-xs text-gray-600 mt-0.5">
                            Lays groundwork for future control over {pillar.name.toLowerCase()}
                            {pillar.source_url && (
                              <a 
                                href={pillar.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="ml-1 text-blue-600 hover:underline"
                              >
                                [source]
                              </a>
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Citation Sources */}
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Data Sources:</h4>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <ul className="text-xs text-gray-700 space-y-1">
                    {pillars.filter(p => p.source_name).map((pillar, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-blue-600 mr-1">•</span>
                        <span>
                          <strong>{pillar.name}</strong>: {pillar.source_name}
                          {pillar.source_organization && ` (${pillar.source_organization})`}
                          {pillar.source_url && (
                            <a 
                              href={pillar.source_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="ml-1 text-blue-600 hover:underline"
                            >
                              [link]
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                    {pillars.filter(p => p.source_name).length === 0 && (
                      <li>
                        <span className="text-gray-500 italic">
                          Sources: <a href="https://freedomhouse.org/report/freedom-world" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Freedom House</a>, <a href="https://v-dem.net/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">V-Dem Institute</a>
                        </span>
                      </li>
                    )}
                    <li className="text-gray-500 text-right pt-1">
                      Analysis as of: {new Date().toLocaleDateString()}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // Use the CardContainer for consistent styling
  return (
    <CardContainer
      header={header}
      footer={footer}
      defaultExpanded={defaultExpanded}
      expandable={!embedded}
      embedded={embedded}
    >
      {content}
    </CardContainer>
  );
};

export default DemocracyThreatCard;